import { useEffect, useRef } from 'react';

/**
 * A custom hook that returns the previous value of a variable.
 *
 * This hook keeps track of the previous value of the provided variable
 * across renders. It's useful for comparing current and previous values
 * in effects or for any logic that needs to know how a value has changed.
 *
 * @template T The type of the value being tracked
 * @param {T} value The current value
 * @returns {T | undefined} The previous value (undefined on first render)
 *
 * @example
 * const Counter = () => {
 *   const [count, setCount] = useState(0);
 *   const prevCount = usePreviousValue(count);
 *
 *   useEffect(() => {
 *     if (prevCount !== undefined && count > prevCount) {
 *       console.log('Counter increased!');
 *     }
 *   }, [count, prevCount]);
 *
 *   return (
 *     <div>
 *       <p>Current count: {count}</p>
 *       <p>Previous count: {prevCount ?? 'N/A'}</p>
 *       <button onClick={() => setCount(c => c + 1)}>Increment</button>
 *     </div>
 *   );
 * };
 */
export const usePreviousValue = <T>(value: T): T | undefined => {
    const ref = useRef<T | undefined>(undefined);

    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
};
